import React, { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import * as LoyaltyService from '~/services/LoyaltyService';

import { type ManualAccrual, type ManualAccrualsRetryRequestBody } from '~/types/loyaltyContract';

/**
 * The service accepts at most one of `accrueWithEstimate` / `accrueWithPending` and rejects the
 * request outright if both are set, so they are a single choice here rather than checkboxes an
 * operator could tick together.
 */
type OverrideMode = 'none' | 'accrueWithEstimate' | 'accrueWithPending';

const OVERRIDE_OPTIONS: Array<{ value: OverrideMode; label: string; description: string }> = [
  {
    value: 'none',
    label: 'Normal retry',
    description: 'Re-run the accrual and credit whatever the usual checks decide.',
  },
  {
    value: 'accrueWithEstimate',
    label: 'Accrue with estimate',
    description:
      "Credit the points the earn engine calculates now, even if they disagree with the customer's pending points.",
  },
  {
    value: 'accrueWithPending',
    label: 'Accrue with pending',
    description: 'Credit the pending points the customer was promised at checkout, even if the engine disagrees.',
  },
];

const MAX_REASON_LENGTH = 500;

interface RetryManualAccrualsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedAccruals?: Array<ManualAccrual>;
  onSuccess?: () => void;
}

export default function RetryManualAccrualsDialog({
  open,
  onClose,
  selectedAccruals = [],
  onSuccess,
}: RetryManualAccrualsDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [overrideMode, setOverrideMode] = useState<OverrideMode>('none');
  const [reestimatePendingPoints, setReestimatePendingPoints] = useState(false);
  const [reimportBigQueryData, setReimportBigQueryData] = useState(false);
  const [reason, setReason] = useState('');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setOverrideMode('none');
      setReestimatePendingPoints(false);
      setReimportBigQueryData(false);
      setReason('');
    }
  }, [open]);

  const retryMutation = useMutation({
    mutationFn: (params: ManualAccrualsRetryRequestBody) => LoyaltyService.retryManualAccruals(params),
    onSuccess: (data) => {
      const { result } = data;
      enqueueSnackbar(
        `Retry completed: ${result.totalItemsCount - result.failedItemsCount}/${result.totalItemsCount} successful`,
        { variant: 'success' },
      );
      if (result.failedItemsCount > 0) {
        enqueueSnackbar(`${result.failedItemsCount} items failed to retry`, { variant: 'warning' });
      }
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(`Error retrying accruals: ${error?.message || 'Unknown error'}`, { variant: 'error' });
    },
  });

  const handleRetry = async (): Promise<void> => {
    const trimmedReason = reason.trim();
    const params: ManualAccrualsRetryRequestBody = {
      itemIds: selectedAccruals.map((a) => a.itemId),
      accrueWithEstimate: overrideMode === 'accrueWithEstimate' || undefined,
      accrueWithPending: overrideMode === 'accrueWithPending' || undefined,
      reestimatePendingPoints: (!overriding && reestimatePendingPoints) || undefined,
      reimportBigQueryData: (!overriding && reimportBigQueryData) || undefined,
      reason: trimmedReason || undefined,
    };

    retryMutation.mutate(params);
  };

  const handleClose = (): void => {
    if (!retryMutation.isPending) {
      onClose();
    }
  };

  // Both refresh steps persist what they recompute, so combining them with an override is either
  // a no-op on the credited figure or silently changes what the override means. Run one as a
  // normal retry first, then override if it still does not land where you want.
  const overriding = overrideMode !== 'none';

  const itemCount = selectedAccruals.length;
  const reasonTooLong = reason.trim().length > MAX_REASON_LENGTH;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Retry Manual Accruals</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          {itemCount > 0 && (
            <Alert severity="info">
              You have selected {itemCount} accrual{itemCount > 1 ? 's' : ''} to retry.
            </Alert>
          )}

          <Box>
            <FormControl>
              <FormLabel id="retry-override-mode-label">What should this retry credit?</FormLabel>
              <RadioGroup
                aria-labelledby="retry-override-mode-label"
                value={overrideMode}
                onChange={(e) => setOverrideMode(e.target.value as OverrideMode)}
              >
                {OVERRIDE_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    disableTypography
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Before retrying
            </Typography>
            {overriding && (
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Not available when you are choosing what to credit. These refresh the data and keep the result, so run
                one on a normal retry first if the figures look stale.
              </Typography>
            )}
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reestimatePendingPoints && !overriding}
                    disabled={overriding}
                    onChange={(e) => setReestimatePendingPoints(e.target.checked)}
                  />
                }
                label="Recalculate the pending points first"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reimportBigQueryData && !overriding}
                    disabled={overriding}
                    onChange={(e) => setReimportBigQueryData(e.target.checked)}
                  />
                }
                label="Re-import the order data from BigQuery first"
              />
            </Stack>
          </Box>

          <Divider />

          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            error={reasonTooLong}
            helperText={
              reasonTooLong
                ? `Keep this under ${MAX_REASON_LENGTH} characters.`
                : 'Recorded against each accrual so the next person can see why this was run.'
            }
          />

          {retryMutation.isError && (
            <Alert severity="error">Error: {retryMutation.error?.message || 'Failed to retry accruals'}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={retryMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleRetry}
          variant="contained"
          color="primary"
          disabled={retryMutation.isPending || itemCount === 0 || reasonTooLong}
        >
          {retryMutation.isPending ? 'Retrying...' : 'Retry Accruals'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
